import { defineNuxtPlugin, useRouter } from '#imports'
import { nextTick } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'

/**
 * Promise that resolves when the current cross-slug view transition's
 * crossfade completes. `null` when no transition is in progress or
 * when it's a same-slug navigation (only query params changed).
 *
 * Chip animations check this to defer during cross-slug page transitions
 * (the crossfade handles the visual transition, chips animate after).
 * Same-slug grade switches (same UUID) get the full chip swap animation.
 */
let activeViewTransition: Promise<void> | null = null

/**
 * @note Plain function reading a module-level let, safe to define outside of the plugin.
 * because the plugin is only executed on the client side (`.client.ts`)
 */
export function getActiveViewTransition(): Promise<void> | null {
  return activeViewTransition
}

/**
 * This plugin is used to add a viewTransition to PDPs navigation
 * when the user clicks on a picker. It only applies to PDPs when:
 * - we're in the experiment.ppOptimiseGrades experiment
 * - the user is clicking on a picker (`query.pickerClicked`)
 * - the user is not in a reduced motion mode
 * - the navigation happens on the client side
 *
 * For reference, we apply a similar logic than the official viewTransition plugin from Nuxt:
 * @see https://github.com/nuxt/nuxt/blob/579db312d568fe139daa5f64e3c2467d486ea7e2/packages/nuxt/src/app/plugins/view-transitions.client.ts
 */
export default defineNuxtPlugin(function pdpPickerViewTransition(nuxtApp) {
  if (!document.startViewTransition) {
    return
  }

  const router = useRouter()

  let finishTransition: (() => void) | undefined
  let abortTransition: (() => void) | undefined

  const PDP_ROUTE_NAMES = new Set([
    PRODUCT_ROUTES.MASTER_PRODUCT_PAGE,
    PRODUCT_ROUTES.VARIANT_PRODUCT_PAGE,
  ])

  const isPDP = (route: RouteLocationNormalized) => {
    return PDP_ROUTE_NAMES.has(route.name as string)
  }

  const resetTransitionState = () => {
    finishTransition = undefined
    abortTransition = undefined
  }

  router.beforeResolve(async (to, from) => {
    if (!isPDP(to) || !isPDP(from)) {
      return undefined
    }

    const isPickerNavigation = to.query.pickerClicked === 'true'

    const experiments = useExperiments()
    const isExperimentActive =
      experiments['experiment.ppOptimiseGrades'] === 'withOptimisedGrades' ||
      experiments['experiment.ppEsimCallout'] === 'withCallout'

    if (
      !isPickerNavigation ||
      !isExperimentActive ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined
    }

    // Cross-slug navigation: different product variant (UUID changed).
    // Chip animations will defer until the crossfade completes.
    const isCrossSlug = to.params.uuid !== from.params.uuid

    const promise = new Promise<void>((resolve, reject) => {
      finishTransition = resolve
      abortTransition = reject
    })

    let changeRoute: () => void
    const ready = new Promise<void>((resolve) => {
      changeRoute = resolve
    })

    const transition = document.startViewTransition(() => {
      // Signal that the old state has been captured and navigation can proceed.
      // Return `promise` so the browser waits for the new DOM before animating.
      changeRoute?.()

      return promise
    })

    // Only expose the transition for cross-slug navigations so chip
    // animations defer. Same-slug grade switches keep the full swap animation.
    if (isCrossSlug) {
      activeViewTransition = transition.finished
        .catch(Function.prototype as () => void)
        .then(() => undefined)
    }

    transition.finished.catch(Function.prototype as () => void).finally(() => {
      activeViewTransition = null
      resetTransitionState()
    })

    // Block navigation until the view transition has captured the old state.
    // `changeRoute` is called from the view transition callback, which runs
    // after the browser has taken a snapshot of the current DOM.
    return ready
  })

  router.afterEach((to, from) => {
    if (!finishTransition) return

    const isCrossSlug = to.params.uuid !== from.params.uuid

    if (isCrossSlug) {
      // Cross-slug: the new product data needs time to render.
      // Use a MutationObserver to detect when Vue has flushed the
      // new content into the DOM, then signal "new state ready".
      // rAF does NOT work here — the View Transition API blocks
      // rendering while waiting for the promise, so rAF never fires.
      const observer = new MutationObserver(() => {
        observer.disconnect()
        nextTick(() => {
          finishTransition?.()
          resetTransitionState()
        })
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      })

      // Safety timeout to avoid Chrome's 4s view transition freeze.
      setTimeout(() => {
        observer.disconnect()
        finishTransition?.()
        resetTransitionState()
      }, 3000)
    } else {
      // Same-slug: DOM updates synchronously (just query param change).
      finishTransition?.()
      resetTransitionState()
    }
  })

  nuxtApp.hook('vue:error', () => {
    abortTransition?.()
    resetTransitionState()
  })
})
